import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import pdfjsWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";
import { C } from "../theme";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Renders a PDF File/Blob to stacked canvases so the preview is reliable across
// mobile browsers (iOS Safari included) with native scroll + button zoom.
export default function PdfPreview({ file, zoom = 1 }) {
  const containerRef = useRef(null);
  const docRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const buf = await file.arrayBuffer();
        const doc = await pdfjsLib.getDocument({ data: buf }).promise;
        if (cancelled) return;
        docRef.current = doc;
        setNumPages(doc.numPages);
      } catch (e) { setErr("Could not render the inline preview"); }
    })();
    return () => { cancelled = true; };
  }, [file]);

  useEffect(() => {
    const doc = docRef.current;
    const cont = containerRef.current;
    if (!doc || !cont || !numPages) return;
    let cancelled = false;
    (async () => {
      cont.innerHTML = "";
      const containerW = Math.min(Math.max(240, cont.clientWidth - 16), 900);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      for (let i = 1; i <= doc.numPages; i++) {
        if (cancelled) return;
        const page = await doc.getPage(i);
        const base = page.getViewport({ scale: 1 });
        const fit = (containerW / base.width) * zoom;
        const vp = page.getViewport({ scale: fit * dpr });
        const canvas = document.createElement("canvas");
        canvas.width = vp.width; canvas.height = vp.height;
        canvas.style.width = base.width * fit + "px";
        canvas.style.height = base.height * fit + "px";
        canvas.style.maxWidth = zoom > 1 ? "none" : "100%";
        canvas.style.display = "block";
        canvas.style.margin = "0 auto 10px";
        canvas.style.background = "#fff";
        canvas.style.borderRadius = "4px";
        canvas.style.boxShadow = "0 2px 10px rgba(0,0,0,0.4)";
        cont.appendChild(canvas);
        try { await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise; } catch (e) {}
      }
    })();
    return () => { cancelled = true; };
  }, [numPages, zoom]);

  if (err) {
    return (
      <div data-testid="report-view-frame" style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center", color: C.muted, background: "#525659" }}>
        {err}. Use “Full Screen” or “Save to Device” below.
      </div>
    );
  }
  return <div ref={containerRef} data-testid="report-view-frame" style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 8, WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", touchAction: "pan-x pan-y pinch-zoom", background: "#525659" }} />;
}
